import { ArrowRight, ScanLine } from 'lucide-react';
import QrCodeImage, { type QrCodeState } from 'app/javascript/components/shared/QrCodeImage';
import type { FridgeCompany, FridgeProduct } from 'app/javascript/services/fridge';
import { formatCurrency } from 'app/javascript/utils/currency';

// How many product rows the drawn purchase screen shows, and how many of them
// come pre-marked: enough to read as "pick what you took", not a second menu.
const PREVIEW_ROWS = 6;
const PREVIEW_MARKED = 2;

export const FRIDGE_HOW_TO_STYLES = `
  .howto-phone { width: 74mm; height: 116mm; }
  .howto-qr { width: 50mm; height: 50mm; }
  .howto-logo { width: 15mm; height: 15mm; }
  @media print {
    .howto-phone { width: 74mm !important; height: 104mm !important; }
    .howto-qr { width: 44mm !important; height: 44mm !important; }
  }
  /* No footer on this sheet, so it does not reserve the footer band. */
  @media print { .poster-sheet--second { padding-bottom: 8mm !important; } }
`;

const STEPS = [
  'Toque no link que aparece na câmera.',
  'Marque o que você pegou.',
  'Pague o Pix que a Geladeira mostrar.',
];

/**
 * The second sheet of the fridge poster: how to buy. Students see a QR on a
 * fridge and open their bank by reflex, so this page shows the gesture instead
 * of explaining it — a phone in camera mode holding the real QR, the purchase
 * screen beside it, and the Pix only at the end. Black on purpose: it is the
 * one sheet on the fridge that is not a menu.
 */
export default function FridgeHowToSheet({
  company,
  products,
  url,
  onQrStateChange,
}: {
  company: FridgeCompany;
  products: FridgeProduct[];
  url: string;
  onQrStateChange?: (state: QrCodeState) => void;
}) {
  const preview = products.slice(0, PREVIEW_ROWS);
  const total = preview.slice(0, PREVIEW_MARKED).reduce((sum, product) => sum + product.price_cents, 0);

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex items-center gap-4">
        {company.logo_url && (
          <img src={company.logo_url} alt="" className="howto-logo shrink-0 rounded-2xl border border-slate-700 bg-white object-cover" />
        )}
        <span className="font-bebas text-4xl leading-none tracking-wide text-white">{company.name}</span>
      </div>

      <h1 className="mt-10 font-bebas text-7xl leading-[0.9] tracking-wide text-white print:mt-8 print:text-6xl">
        Aponte a câmera
        <br />
        do celular aqui.
      </h1>

      <div className="mt-10 flex items-center justify-center gap-4 print:mt-8">
        <CameraPhone url={url} onQrStateChange={onQrStateChange} />
        <ArrowRight className="h-7 w-7 shrink-0 text-yellow-400" strokeWidth={2.4} aria-hidden />
        <StorePhone products={preview} total={total} />
      </div>

      <ol className="mt-10 flex flex-col gap-3.5 print:mt-8">
        {STEPS.map((step, index) => (
          <li key={step} className="flex items-baseline gap-3.5">
            <span className="w-6 shrink-0 font-bebas text-2xl leading-none text-yellow-400">{index + 1}</span>
            <span className="text-xl font-medium leading-snug text-white">{step}</span>
          </li>
        ))}
      </ol>

      <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-slate-400">
        Este código não é um Pix, então o app do banco não lê. O QR do Pix aparece no seu celular no fim.
      </p>
    </div>
  );
}

// A phone in camera mode. The QR inside it is the real one, black on white,
// with its quiet zone clear of the yellow viewfinder corners.
function CameraPhone({ url, onQrStateChange }: { url: string; onQrStateChange?: (state: QrCodeState) => void }) {
  const corner = 'absolute h-[30px] w-[30px] border-yellow-400';

  return (
    <div className="howto-phone flex shrink-0 flex-col rounded-[40px] border-[3px] border-slate-700 bg-black p-3.5">
      {/* No decorative gradient here: Safari prints a transparent radial as an
          opaque disc, and a positioned layer paints over the QR. */}
      <div className="relative flex flex-1 items-center justify-center overflow-hidden rounded-[26px] bg-gray-900">
        {/* Above every sibling on purpose: nothing drawn on this screen may ever paint over the code. */}
        <div className="howto-qr relative z-10 mb-9 rounded-xl bg-white p-2">
          <QrCodeImage value={url} size={260} className="h-full w-full" alt="QR Code da Geladeira" onStateChange={onQrStateChange} />
        </div>
        <span className={`${corner} left-5 top-5 border-l-[5px] border-t-[5px]`} aria-hidden />
        <span className={`${corner} right-5 top-5 border-r-[5px] border-t-[5px]`} aria-hidden />
        <span className={`${corner} bottom-[74px] left-5 border-b-[5px] border-l-[5px]`} aria-hidden />
        <span className={`${corner} bottom-[74px] right-5 border-b-[5px] border-r-[5px]`} aria-hidden />
        <div className="absolute inset-x-5 bottom-5 flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-yellow-400 px-3 py-2.5 text-sm font-bold text-slate-950">
          <ScanLine className="h-4 w-4" strokeWidth={2.4} aria-hidden />
          Abrir blackbelts.app
        </div>
      </div>
    </div>
  );
}

// The purchase screen, drawn with the academy's own products so the student
// recognizes it when it opens. Only the button that ends the flow is yellow.
function StorePhone({ products, total }: { products: FridgeProduct[]; total: number }) {
  return (
    <div className="howto-phone flex shrink-0 flex-col rounded-[40px] border-[3px] border-slate-700 bg-black p-3.5">
      <div className="flex flex-1 flex-col overflow-hidden rounded-[26px] bg-slate-900 px-3.5 pb-3 pt-3.5">
        <span className="font-bebas text-2xl leading-none text-white">Geladeira</span>
        <div className="mt-2 flex flex-col">
          {products.length === 0 ? (
            <div className="border-b border-slate-800 py-2.5 text-xs text-slate-400">Seus produtos aparecem aqui.</div>
          ) : (
            products.map((product, index) => {
              const marked = index < PREVIEW_MARKED;
              return (
                <div key={product.id} className="flex items-center justify-between gap-2 border-b border-slate-800 py-2 text-xs text-slate-200">
                  <span className="truncate">{product.name}</span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="text-slate-400">{formatCurrency(product.price_cents)}</span>
                    <span
                      className={`flex h-[22px] w-[22px] items-center justify-center rounded-md text-xs font-bold ${
                        marked ? 'bg-yellow-400 text-slate-950' : 'bg-slate-800 text-slate-400'
                      }`}
                    >
                      {marked ? 1 : '+'}
                    </span>
                  </span>
                </div>
              );
            })
          )}
        </div>
        <div className="mt-auto rounded-[10px] bg-yellow-400 px-3 py-2.5 text-center text-[13px] font-bold text-slate-950">
          Gerar Pix · {formatCurrency(total)}
        </div>
      </div>
    </div>
  );
}
